import { UIHeading1 } from '@cian/ui-kit';
import { IconActionChevronDown16, IconActionChevronUp16 } from '@cian/ui-kit-design-tokens/icons';
import cx from 'clsx';
import * as React from 'react';

import * as style from './CardSectionShutter.css';

export interface IShutterProps {
  hideForPrint?: boolean;
  title: string;
  children: React.ReactNode;
  fullMaxHeight?: boolean;
}

const getToggles = ({ title }: { title: string }) => {
  return {
    open: (
      <>
        <UIHeading1 as="h2">{title}</UIHeading1>
        <IconActionChevronUp16 />
      </>
    ),
    close: (
      <>
        <UIHeading1 as="h2">{title}</UIHeading1>
        <IconActionChevronDown16 />
      </>
    ),
  };
};

export function CardSectionShutter(props: IShutterProps) {
  const ref = React.createRef<HTMLDivElement>();
  const [open, setOpen] = React.useState(false);
  const [height, setHeightState] = React.useState(0);
  const { children, title, hideForPrint } = props;

  const handleChange = () => {
    setOpen(!open);
  };

  const getToggleElement = (opened: boolean) => {
    const toggles = getToggles({ title });

    return opened ? toggles.open : toggles.close;
  };

  const setHeight = () => {
    const elem = ref.current;
    /* istanbul ignore next */
    if (!elem) {
      return;
    }

    const { height } = elem.getBoundingClientRect();

    setHeightState(height);
  };

  React.useEffect(() => {
    setHeight();
    window.addEventListener('resize', setHeight, false);

    return () => {
      window.removeEventListener('resize', setHeight, false);
    };
  }, []);

  if (!children) {
    return null;
  }

  return (
    <div
      data-mark="CardSectionShutter"
      className={cx(
        style['container'],
        hideForPrint && style['hide-for-print'],
        props.fullMaxHeight && style['container-fullsize'],
      )}
    >
      <div
        className={cx(style['toggle'], props.fullMaxHeight && style['toggle-fullsize'])}
        onClick={handleChange}
        data-id="toggle"
        data-mark="ShutterToggle"
      >
        {getToggleElement(open)}
      </div>

      <div
        className={style['content']}
        data-testid="shutter"
        style={{ maxHeight: open ? (props.fullMaxHeight ? '100%' : `${height}px`) : `${0}px` }}
      >
        <div className={style['layout']} data-testid="content" ref={ref}>
          {children}
        </div>
      </div>
    </div>
  );
}
